import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'

import billing from './billing'
import user from './user'
import jobs from './jobs'
import notifications from './notifications'
import { escapeString, errorHandlingAppSync, buildJobToCompare, defaultConfig } from '../services/helpers'

Vue.use(Vuex)

const store = new Vuex.Store({
  state: {
    config: {
      API_URL: process.env.API_URL,
      API_TRIALS_URL: process.env.API_TRIALS_URL,
      AUTH0_CALLBACK_URL: process.env.AUTH0_CALLBACK_URL,
      TARGET_VERSION: '1.2.0'
    },
    jobs: {
      sourceFiles: []
    },
    gettingFileURL: false,
    files: [],
    filesNested: {},
    filesCompare: {},
    nestedFilesCompare: {},
    materials: [],
    printers: [],
    customMaterials: [],
    customPrinters: [],
    filesNestedJobName: '',
    jobsPagination: {
      loading: true,
      nextToken: null
    },
    filesPagination: {
      loading: true,
      filesOnPage: 10,
      loadedByIteration: 0,
      nextToken: null,
      isAllLoaded: false
    },
    jobsProgress: {
      loaded: 0,
      total: 0
    },
    fileUrls: {},
    auth: null,
    isAuthenticated: false,
    createConfig: null,
    updateTimers: [],
    webGL: false
  },
  actions: {
    getConfig: ({state, commit}) => {
      if (window.location.hostname !== 'local.atlas3d.xyz') {
        commit('SET_URL_CONFIG', {
          API_URL: window.location.origin + '/api',
          API_TRIALS_URL: window.location.origin + '/api',
          AUTH0_CALLBACK_URL: window.location.origin + '/callback'
        })
      }
    },
    getDownloadURL: ({state}, {filename, sourceFilePathKey, jobPathKey, jobOutputPathKey}) => {
      return axios.post(
        `${state.config.API_URL}/files/download`,
        {
          filename: filename,
          sourceFilePathKey: sourceFilePathKey,
          jobPathKey: jobPathKey,
          jobOutputPathKey: jobOutputPathKey
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    getURLData: ({state}, {url, binary, blob}) => {
      if (binary) {
        return axios.get(url, {responseType: 'arraybuffer'})
      } else if (blob) {
        return axios.get(url, {responseType: 'blob'})
      } else {
        return axios.get(url)
      }
    },
    refreshUrl: ({dispatch, state, commit}, payload) => {
      if (state.fileUrls[payload.jobOutputPathKey] == null) {
        return dispatch('getDownloadURL', payload).then(response => {
          if (payload.jobOutputPathKey) {
            commit('SET_URL', { filePathKeyAndName: payload.jobOutputPathKey + payload.filename, url: response.data.url })
          } else {
            commit('SET_URL', { filePathKeyAndName: payload.sourceFilePathKey + payload.filename, url: response.data.url })
          }
          return response.data.url
        })
      }
      const timerId = setInterval(() => {
        dispatch('getDownloadURL', payload).then(response => {
          if (payload.jobOutputPathKey) {
            commit('SET_URL', { filePathKeyAndName: payload.jobOutputPathKey + payload.filename, url: response.data.url })
          } else {
            commit('SET_URL', { filePathKeyAndName: payload.sourceFilePathKey + payload.filename, url: response.data.url })
          }
        })
      }, 15 * 60 * 1000)
      commit('SET_UPDATE_TIMER', timerId)
    },
    async refreshFiles ({rootState, state, commit}, rule) {
      if (rule === 'notFirstInit') return
      if (rule === 'resetFiles') commit('RESET_UPLOADED_FILES')

      state.filesPagination.loading = true
      let loadedFiles = []

      do {
        if (state.filesPagination.isAllLoaded) break

        let token = state.filesPagination.nextToken
        let limit = state.filesPagination.filesOnPage

        let response = await axios.post(
          process.env.APPSYNC_URL,
          {
            query:
            `query {
              user: sourceFileByUser(
                limit: ${limit}
                userId: "${rootState.user.siemensId}"
                sortDirection: DESC
                nextToken: ${token ? `"${token}"` : null}
                filter: {
                  isDeleted: { eq: false }
                }
              ) {
                sourceFiles: items {
                  id
                  name
                  pathKey
                  isVirus
                  isInvalidStlFile
                  fixOperation {
                    status
                    createdAt
                    downloadedAt
                    id
                    updatedAt
                  }
                }
                nextToken
              }
            }`
          },
          {
            headers: {
              Authorization: `${state.user.token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        response = errorHandlingAppSync(response)

        if (response) {
          const files = response.data.user.sourceFiles
          const nextToken = response.data.user.nextToken
          loadedFiles = loadedFiles.concat(files)

          if (nextToken === null) commit('SET_FILES_IS_ALL_LOADED')
          commit('SET_FILES_NEXT_TOKEN', nextToken)
          commit('SET_LOAD_BY_ITERATION', files.length)
        }
      } while (state.filesPagination.loadedByIteration < state.filesPagination.filesOnPage)

      commit('SET_FILES', { files: loadedFiles })
      commit('RESET_LOAD_BY_ITERATION')
      state.filesPagination.loading = false
    },
    loadSourceFile ({state, commit}, { id, forceRefresh }) {
      return new Promise((resolve, reject) => {
        const sourceFile = state.files.find((file) => file.id === id)
        if (sourceFile && !forceRefresh) {
          return resolve(sourceFile)
        }

        axios.post(
          process.env.APPSYNC_URL,
          {
            query: `
            query {
              sourceFile: getSourceFile(id: "${id}") {
                id
                name
                pathKey
                fixOperation {
                  status
                }
              }
            }`
          },
          {
            headers: {
              Authorization: `${state.user.token}`,
              'Content-Type': 'application/json'
            }
          }
        ).then((response) => {
          response = errorHandlingAppSync(response)
          if (response) {
            if (sourceFile) {
              commit('UPDATE_FILE', response.data.sourceFile)
            } else {
              commit('SET_FILE', response.data.sourceFile)
            }
            resolve(response.data.sourceFile)
          }
        }, reject)
      })
    },
    checkSourceFile: async ({commit, state, rootState}, payload) => {
      return new Promise((resolve, reject) => {
        axios.post(
          process.env.APPSYNC_URL,
          {
            query: `query {
                user: sourceFileByUser(
                  userId: "${rootState.user.siemensId}",
                  sortDirection: DESC,
                  nextToken: null
                  filter: {
                    isDeleted: { eq: false },
                    pathKey: { eq: "${payload.pathKey}" }
                  }
                ) {
                  sourceFiles: items {
                    id
                    name
                    pathKey
                    isVirus
                    isInvalidStlFile
                    fixOperation {
                      status
                      createdAt
                      downloadedAt
                      id
                      updatedAt
                    }
                  }
                  nextToken
                }
              }`
          },
          {
            headers: {
              Authorization: `${state.user.token}`,
              'Content-Type': 'application/json'
            }
          }
        ).then((response) => {
          response = errorHandlingAppSync(response)
          if (response) {
            if (response.data.user.sourceFiles.length) {
              commit('SET_FIRST_FILE', response.data.user.sourceFiles[0])
            }
            resolve(response.data.user.sourceFiles)
          }
        }, reject)
      })
    },
    submitJob ({state, commit}, payload) {
      let data = {
        jobPathKey: payload.jobPathKey,
        analysisId: payload.analysisId,
        objectiveId: payload.objectiveId
      }
      if (payload.hasOwnProperty('outputFilePathKey') && payload.hasOwnProperty('filename')) {
        data.outputFilePathKey = payload.outputFilePathKey
        data.filename = payload.filename
      }
      return axios.post(
        `${state.config.API_URL}/jobs/submit`,
        data,
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`,
            'Content-Type': 'application/json'
          }
        }
      )
    },
    updateJob ({state, commit}, payload) {
      if ('AdditionalConfig' in payload.config) {
        payload.config.AdditionalConfig =
            payload.config.RestrictedFaces.length > 0 || payload.config.AllowedFaces.length > 0
      }
      let data = {
        jobPathKey: payload.jobPathKey,
        config: payload.config,
        isArchived: false
      }
      if (payload.jobDescription) {
        data.jobDescription = escapeString(payload.jobDescription)
      }
      return axios.post(
        `${state.config.API_URL}/jobs/update`,
        data,
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    createJob ({state, commit}, payload) {
      payload.config.AdditionalConfig =
        payload.config.RestrictedFaces.length > 0 || payload.config.AllowedFaces.length > 0
      return axios.post(
        `${state.config.API_URL}/jobs/create`,
        {
          sourceFilePathKey: payload.sourceFilePathKey,
          targetVersion: state.config.TARGET_VERSION,
          config: payload.config
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    createNestedJob ({state, commit}, payload) {
      return axios.post(
        `${state.config.API_URL}/nested/create`,
        {
          config: payload.config,
          nestedJobs: payload.nestedJobs,
          nestedJobName: escapeString(payload.nestedJobName),
          targetVersion: state.config.TARGET_VERSION
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    updateNestedJob ({state, commit}, payload) {
      return axios.post(
        `${state.config.API_URL}/nested/update`,
        {
          nestedJobs: payload.nestedJobs,
          nestedJobName: escapeString(payload.nestedJobName),
          parentPathKey: payload.parentPathKey
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    submitNestedJob ({state, commit}, payload) {
      return axios.post(
        `${state.config.API_URL}/nested/submit`,
        {
          nestedJobs: payload
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    redeemJob ({state, commit}, pathKey) {
      return axios.post(
        `${state.config.API_URL}/joboutputs/redeem`,
        {
          jobOutputPathKey: pathKey
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    uploadFile ({state, commit}, payload) {
      const data = {
        filename: payload.file.name
      }
      if (payload.hasOwnProperty('sourceFilePathKey') && payload.hasOwnProperty('jobPathKey')) {
        data.sourceFilePathKey = payload.sourceFilePathKey
        data.jobPathKey = payload.jobPathKey
      }
      return axios.post(
        `${state.config.API_URL}/files/upload`,
        data,
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      ).then(response => {
        return axios.put(
          response.data.url,
          payload.file,
          {
            headers: {
              'Content-Type': 'application/octet-stream'
            },
            pathKey: response.data.filePathKey
          }
        )
      })
    },
    deleteFile ({state, commit}, file) {
      return axios.post(
        `${state.config.API_URL}/files/delete`,
        {
          sourceFilePathKey: file.pathKey
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      ).then(response => {
        commit('DELETE_FILE', file)
      })
    },
    repairFile ({state, commit}, file) {
      return axios.post(
        `${state.config.API_URL}/files/repair`,
        {
          sourceFilePathKey: file.pathKey,
          config: Object.assign({}, defaultConfig, {
            filename: file.name,
            isRepair: true,
            tetraEps: [file.config.tetraEps],
            tetraMeshSize: [file.config.tetraMeshSize]
          }),
          targetVersion: state.config.TARGET_VERSION
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    generateSupport ({state, commit}, payload) {
      payload.config.AdditionalConfig =
        payload.config.RestrictedFaces.length > 0 || payload.config.AllowedFaces.length > 0
      return axios.post(
        `${state.config.API_URL}/supports/preview`,
        {
          sourceFilePathKey: payload.sourceFilePathKey,
          targetVersion: state.config.TARGET_VERSION,
          config: payload.config
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    checkElementSize ({state, commit}, payload) {
      return axios.post(
        `${state.config.API_URL}/actions/check-size`,
        {
          sourceFilePathKey: payload.sourceFilePathKey,
          targetVersion: state.config.TARGET_VERSION,
          config: payload.config
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    remeshPart ({state, dispatch}, payload) {
      return axios.post(
        `${state.config.API_URL}/actions/remesh`,
        {
          sourceFilePathKey: payload.sourceFilePathKey,
          targetVersion: state.config.TARGET_VERSION,
          config: payload.config
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    paintByCurvature ({state, dispatch}, payload) {
      return axios.post(
        `${state.config.API_URL}/actions/paint`,
        {
          sourceFilePathKey: payload.sourceFilePathKey,
          targetVersion: state.config.TARGET_VERSION,
          config: {...payload.config, filename_supp: ''}
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    async getJobsOrdered ({rootState, state, commit}) {
      state.jobsPagination.loading = true
      const loadedJobs = {
        sourceFiles: []
      }

      do {
        let token = state.jobsPagination.nextToken

        let response = await axios.post(
          process.env.APPSYNC_URL,
          {
            query: `query {
                user: sourceFileByUser(
                  userId: "${rootState.user.siemensId}",
                  sortDirection: DESC,
                  nextToken: ${token ? `"${token}"` : null}
                  filter: {
                    isDeleted: { eq: false }
                  }
                ) {
                  sourceFiles: items {
                    id
                    pathKey
                    name
                    jobs (sortDirection: DESC) {
                      items {
                        id
                        pathKey
                        parentPathKey
                        nestedJobName
                        jobDescription
                        config
                        jobOutputs (sortDirection: DESC) {
                          items {
                            isArchived
                          }
                        }
                        isNested
                        isArchived
                        createdAt
                        updatedAt
                      }
                    }
                  }
                  nextToken
                }
              }`
          },
          {
            headers: {
              Authorization: `${state.user.token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        response = errorHandlingAppSync(response)
        // calculate count of archived/unarchived jobs
        const nextToken = response.data.user.nextToken

        response.data.user.sourceFiles = response.data.user.sourceFiles.map(file => {
          let archived, unarchived

          const jobs = file.jobs.map(job => {
            if (job.jobOutputs.length) {
              archived = job.jobOutputs.filter(job => job.isArchived).length
              unarchived = job.jobOutputs.length - archived
            } else {
              archived = 0
              unarchived = 0
            }

            const result = {
              ...job,
              jobOutputs: null,
              jobOutputsCount: job.jobOutputs.length,
              archived: {
                count: archived
              },
              unarchived: {
                count: unarchived
              }
            }
            return result
          })
          return {
            ...file,
            jobs
          }
        })
        loadedJobs.sourceFiles = loadedJobs.sourceFiles.concat(response.data.user.sourceFiles)

        commit('SET_JOBS_NEXT_TOKEN', nextToken)
      } while (state.jobsPagination.nextToken)

      commit('SET_JOBS', {user: loadedJobs})
    },
    getJobsOutputs ({state, commit, dispatch}, {sourceFilePathKey, ids}) {
      const jobsById = ids.map(id => {
        return axios.post(
          process.env.APPSYNC_URL,
          {
            query:
            `query {
              Job: getJob (id: "${id}") {
                id
                pathKey
                createdAt
                updatedAt
                jobDescription
                jobOutputs {
                  items {
                    id
                    status
                    progress
                    pathKey
                    isArchived
                    subscription {
                      id
                  }
                  pathKey
                  analysisId
                  objectiveId
                  outputFiles(limit: 10000) {
                    items {
                        name
                        id
                      }
                    }
                  }
                }
              }
            }`
          },
          {
            headers: {
              Authorization: `${state.user.token}`,
              'Content-Type': 'application/json'
            }
          }
        )
      })
      return Promise.all(jobsById)
        .then((response) => {
          const mappingResponse = {
            data: {
              data: {
                allJobs: []
              }
            }
          }
          for (let res of response) {
            if (res.data && res.data.data && res.data.data.Job) {
              mappingResponse.data.data.allJobs.push(res.data.data.Job)
            }
          }
          response = errorHandlingAppSync(mappingResponse)
          const jobs = response.data.allJobs
          jobs.forEach((job) => {
            commit('SET_JOB_OUTPUTS', job)
          })
          return jobs
        })
    },
    getJobOutputs ({state, commit, dispatch}, {jobPathKey, sourceFilePathKey}) {
      return axios.post(
        process.env.APPSYNC_URL,
        {
          query:
            `query {
              Job: jobByPathKey(
                pathKey: "${jobPathKey}"
                sortDirection: DESC
                ) {
                item: items {
                  id
                  pathKey
                  config
                  parentPathKey
                  nestedJobName
                  isNested
                  isArchived
                  createdAt
                  updatedAt
                  jobDescription
                  jobOutputs {
                    items {
                      id
                      status
                      progress
                      pathKey
                      isArchived
                      subscription {
                        id
                      }
                      pathKey
                      analysisId
                      objectiveId
                      outputFiles(limit: 10000) {
                        items {
                          name
                          id
                        }
                      }
                    }
                  }
                }
              }
            }`
        },
        {
          headers: {
            Authorization: `${state.user.token}`,
            'Content-Type': 'application/json'
          }
        }).then(response => {
          response = errorHandlingAppSync(response)
          const jobOutputs = response.data.Job
          const sortOutputFiles = (a, b) => {
            if (a.name.match(/plate_[0-9]+\.stl$/) && b.name.match(/plate_[0-9]+\.stl$/)) {
              return Number(a.name.match(/plate_([0-9]+)\.stl$/)[1]) - Number(b.name.match(/plate_([0-9]+)\.stl$/)[1])
            } else if (a.name.match(/support[0-9]+\.stl$/) && b.name.match(/support[0-9]+\.stl$/)) {
              return Number(a.name.match(/support([0-9]+)\.stl$/)[1]) - Number(b.name.match(/support([0-9]+)\.stl$/)[1])
            } else if (a.name.match(/plate_[0-9]+\.stl$/) && b.name.match(/support[0-9]+\.stl$/)) {
              return -1
            } else if (b.name.match(/plate_[0-9]+\.stl$/) && a.name.match(/support[0-9]+\.stl$/)) {
              return 1
            }
          }
          jobOutputs.jobOutputs.forEach((jobOutput) => {
            let paidFor = !!(jobOutput.credit || jobOutput.subscription) || (jobOutput.analysisId === 0 && jobOutput.objectiveId === 0)
            if (paidFor) {
              jobOutput.outputFiles.sort(sortOutputFiles)
            }
          })
          commit('SET_JOB_OUTPUTS', jobOutputs)
          return new Promise(resolve => {
            resolve(jobOutputs)
          }, reject => {})
        })
    },
    getJob ({state, commit, dispatch}, pathKey) {
      let mainResponse
      return axios.post(
        process.env.APPSYNC_URL,
        {
          query:
            `query {
              Job: jobByPathKey(pathKey: "${pathKey}") {
                item: items {
                  id
                  pathKey
                  config
                  parentPathKey
                  nestedJobName
                  jobDescription
                  isNested
                  isArchived
                  createdAt
                  updatedAt
                  sourceFile {
                    id
                    pathKey
                  }
                  jobOutputs {
                    items {
                      id
                      analysisId
                      objectiveId
                      isArchived
                      pathKey
                      progress
                      status
                      subscription {
                        id
                      }
                      outputFiles(limit: 10000) {
                        items {
                          id
                          name
                        }
                      }
                    }
                  }
                }
              }
            }`
        },
        {
          headers: {
            Authorization: `${state.user.token}`,
            'Content-Type': 'application/json'
          }
        }
      ).then((response) => {
        response = errorHandlingAppSync(response)
        if (response.data.Job.config.AdditionalConfig && response.data.Job.jobOutputs[0] && response.data.Job.jobOutputs[0].status === 'COMPLETED') {
          mainResponse = response
          return dispatch('getDownloadURL', {
            sourceFilePathKey: response.data.Job.sourceFile.pathKey,
            jobPathKey: pathKey,
            filename: 'additional_config.json'
          })
            .then((response) => {
              return dispatch('getURLData', {url: response.data.url})
            })
            .then((data) => {
              mainResponse.data.config = Object.assign(mainResponse.data.config, data.data)
              return mainResponse
            })
        } else {
          commit('SET_JOB', {
            sourceFilePathKey: response.data.Job.sourceFile.pathKey,
            pathKey: response.data.Job.pathKey,
            job: response.data.Job
          })
          return response
        }
      })
    },
    getNestedJobs ({state, commit}, pathKey) {
      return axios.post(
        process.env.APPSYNC_URL,
        {
          query:
          `query {
            allJobs: jobByParentPathKey(parentPathKey: "${pathKey}") {
              items {
                id
                pathKey
                config
                parentPathKey
                importance
                quantity
                isNested
                isArchived
                createdAt
                updatedAt
                sourceFile {
                  id
                  name
                  pathKey
                }
              }
            }
          }`
        },
        {
          headers: {
            Authorization: `${state.user.token}`,
            'Content-Type': 'application/json'
          }
        }
      )
        .then((response) => {
          response = errorHandlingAppSync(response)
          return response
        })
    },
    getJobById ({state, dispatch}, {id, jobPathKey}) {
      return new Promise((resolve, reject) => {
        let job
        let file
        if (state.jobs.sourceFiles) {
          file = state.jobs.sourceFiles.find((sourceFile) => {
            return sourceFile.id === id
          })
          job = file.jobs.find((jobItem) => {
            return jobItem.pathKey === jobPathKey
          })
          if (job) {
            resolve(job)
          }
        } else {
          dispatch('getJob', jobPathKey)
            .then((response) => {
              resolve(response.data.Job)
            })
        }
      })
    },
    getPrintConfig ({state}) {
      return axios.post(
        `${state.config.API_URL}/files/printconfig`,
        {
          targetVersion: state.config.TARGET_VERSION
        },
        {
          headers: {
            Authorization: `Bearer ${state.user.token}`
          }
        }
      )
    },
    clearUpdateTimers ({state, commit}) {
      state.updateTimers.forEach((timer) => {
        clearInterval(timer)
      })
      commit('CLEAR_UPDATE_TIMERS')
    },
    setNestedFile ({state, commit}, payload) {
      commit('SET_NESTED_FILE', payload)
    },
    setJobToCompare ({state, commit}, { job, jobOutput }) {
      const jobToState = buildJobToCompare(job, jobOutput)
      if (jobToState.isNested) {
        commit('SET_NESTED_COMPARE_FILE', jobToState)
      } else {
        commit('SET_COMPARE_FILE', jobToState)
      }
    },
    removeJobFromCompare ({state, commit}, { job, jobOutput }) {
      const jobToState = buildJobToCompare(job, jobOutput)
      if (jobToState.isNested) {
        commit('REMOVE_NESTED_COMPARE_FILE', jobToState)
      } else {
        commit('REMOVE_COMPARE_FILE', jobToState)
      }
    },
    removeCompareData ({state, commit}, payload) {
      commit('REMOVE_COMPARE_DATA', payload)
    },
    resetCompareFiles ({state, commit}) {
      commit('RESET_COMPARE_DATA')
    },
    setNestedFileData ({state, commit}, payload) {
      payload.forEach((job) => {
        commit('SET_NESTED_FILE_DATA', job)
      })
    },
    resetJobs ({commit}) {
      commit('RESET_JOBS')
    },
    submitTrialForm ({state}, { firstName, lastName, email }) {
      return axios.post(
        `${state.config.API_TRIALS_URL}/trial`,
        { firstName, lastName, email }
      )
    }
  },
  mutations: {
    'SET_URL' (state, { filePathKeyAndName, url }) {
      Vue.set(state.fileUrls, filePathKeyAndName, url)
    },
    'SET_JOBS' (state, payload) {
      state.jobs.sourceFiles = state.jobs.sourceFiles.concat(payload.user.sourceFiles)
      state.jobsPagination.loading = false
    },
    'SET_JOB' (state, payload) {
      let fileIndex = -1
      fileIndex = state.jobs.sourceFiles.findIndex((file) => {
        return file.pathKey === payload.sourceFilePathKey
      })
      if (fileIndex > -1) {
        let archived, unarchived
        if (payload.job.jobOutputs.length > 0) {
          archived = payload.job.jobOutputs.filter(job => job.isArchived).length
          unarchived = payload.job.jobOutputs.length - archived
        } else {
          archived = 0
          unarchived = 0
        }
        payload.job.archived = { count: archived }
        payload.job.unarchived = { count: unarchived }
        const jobs = state.jobs.sourceFiles[fileIndex].jobs.slice()
        jobs.push(payload.job)
        state.jobs.sourceFiles.splice(fileIndex, 1, Object.assign({}, state.jobs.sourceFiles[fileIndex], {jobs}))
      }
    },
    'RESET_JOBS' (state) {
      state.jobs.sourceFiles = []
      state.jobsPagination.nextToken = null
    },
    'SET_JOB_OUTPUTS' (state, payload) {
      state.jobs.sourceFiles.forEach((sourceFile) => {
        sourceFile.jobs.forEach((job) => {
          if (job.pathKey === payload.pathKey) {
            Vue.set(job, 'outputsLoaded', true)
            Vue.set(job, 'jobOutputs', payload.jobOutputs)
          }
        })
      })
    },
    'SET_MATERIALS' (state, payload) {
      state.materials = payload
    },
    'SET_PRINTERS' (state, payload) {
      state.printers = payload
    },
    'ADD_CUSTOM_MATERIAL' (state, payload) {
      const findIdx = state.customMaterials.findIndex((material) => {
        return material.name === payload.name
      })
      if (findIdx > -1) {
        state.customMaterials.splice(findIdx, 1, payload)
      } else {
        state.customMaterials.push(payload)
      }
    },
    'ADD_CUSTOM_PRINTER' (state, payload) {
      const findIdx = state.customPrinters.findIndex((printer) => {
        return printer.name === payload.name
      })
      if (findIdx > -1) {
        state.customPrinters.splice(findIdx, 1, payload)
      } else {
        state.customPrinters.push(payload)
      }
    },
    'REMOVE_CUSTOM_MATERIAL' (state, payload) {
      state.customMaterials = state.customMaterials.filter((material) => {
        return material.name !== payload
      })
    },
    'REMOVE_CUSTOM_PRINTER' (state, payload) {
      state.customPrinters = state.customPrinters.filter((printer) => {
        return printer.name !== payload
      })
    },
    'ARCHIVE_JOB' (state, payload) {
      state.jobs.sourceFiles.forEach((file) => {
        file.jobs.forEach((job) => {
          if (job.pathKey === payload.jobPathKey) {
            Vue.set(job, 'isArchived', payload.isArchived)
          }
        })
      })
    },
    'ARCHIVE_JOB_OUTPUT' (state, payload) {
      state.jobs.sourceFiles.forEach((file) => {
        if (file.pathKey === payload.sourceFilePathKey) {
          file.jobs.forEach((job) => {
            if (job.pathKey === payload.jobPathKey) {
              let archivedCount = 0
              let unarchivedCount = 0
              if (payload.isArchived) {
                archivedCount = job.archived.count + 1
                unarchivedCount = job.unarchived.count - 1
              } else {
                archivedCount = job.archived.count - 1
                unarchivedCount = job.unarchived.count + 1
              }
              Vue.set(job.archived, 'count', archivedCount)
              Vue.set(job.unarchived, 'count', unarchivedCount)
              job.jobOutputs.forEach((jobOutput) => {
                if (jobOutput.pathKey === payload.jobOutputPathKey) {
                  Vue.set(jobOutput, 'isArchived', payload.isArchived)
                }
              })
            }
          })
        }
      })
    },
    'REMOVE_JOB' (state, payload) {
      let fileIndex = -1
      let jobIndex = -1
      fileIndex = state.jobs.sourceFiles.findIndex((file) => {
        return file.pathKey === payload.sourceFilePathKey
      })
      if (fileIndex > -1) {
        jobIndex = state.jobs.sourceFiles[fileIndex].jobs.findIndex((job) => {
          return job.pathKey === payload.pathKey
        })
        if (jobIndex > -1) {
          state.jobs.sourceFiles[fileIndex].jobs.splice(jobIndex, 1)
        }
      }
    },
    'SET_JOBS_NEXT_TOKEN' (state, token) {
      state.jobsPagination.nextToken = token
    },
    'SET_FILES' (state, payload) {
      state.files = state.files.concat(payload.files)
    },
    'SET_LOAD_BY_ITERATION' (state, payload) {
      state.filesPagination.loadedByIteration += payload
    },
    'RESET_LOAD_BY_ITERATION' (state) {
      state.filesPagination.loadedByIteration = 0
    },
    'DELETE_FILE' (state, payload) {
      state.files = state.files.filter(el => el.id !== payload.id)
    },
    'RESET_UPLOADED_FILES' (state) {
      state.files = []
      state.filesPagination.isAllLoaded = false
      state.filesPagination.nextToken = null
    },
    'SET_FILES_NEXT_TOKEN' (state, token) {
      state.filesPagination.nextToken = token
    },
    'SET_FILES_IS_ALL_LOADED' (state, token) {
      state.filesPagination.isAllLoaded = true
    },
    'SET_FILE' (state, file) {
      Vue.set(state.files, state.files.length, file)
    },
    'SET_FIRST_FILE' (state, file) {
      state.files = [file, ...state.files]
    },
    'UPDATE_FILE' (state, file) {
      const index = state.files.findIndex((_file) => {
        return _file.id === file.id && file
      })
      Vue.set(state.files, index, file)
    },
    'SET_AUTH' (state, auth) {
      state.auth = auth
    },
    'SET_AUTHENTICATED' (state, authenticated) {
      state.isAuthenticated = authenticated
    },
    'SET_CONFIG' (state, config) {
      Vue.set(state, 'createConfig', config)
    },
    'SET_FILES_TOTAL' (state, payload) {
      Vue.set(state.filesPagination, 'totalFiles', payload)
    },
    'SET_TOTAL_JOBS' (state, payload) {
      Vue.set(state.jobsProgress, 'total', payload)
    },
    'SET_LOADED_JOBS' (state) {
      state.jobsProgress.loaded++
    },
    'RESET_LOADED_JOBS' (state) {
      state.jobsProgress.loaded = 0
    },
    'SET_UPDATE_TIMER' (state, timerId) {
      state.updateTimers.push(timerId)
    },
    'CLEAR_UPDATE_TIMERS' (state) {
      Vue.set(state, 'updateTimers', [])
    },
    'SET_NESTED_FILE' (state, payload) {
      if (state.filesNested[payload.pathKey]) {
        Vue.delete(state.filesNested, payload.pathKey)
      } else {
        Vue.set(state.filesNested, payload.pathKey, {
          id: payload.id,
          name: payload.name,
          pathKey: payload.pathKey,
          quantity: 1,
          importance: 0.5,
          parentPathKey: null
        })
      }
    },
    'SET_NESTED_FILE_DATA' (state, payload) {
      Vue.set(state.filesNested, payload.sourceFile.pathKey, {
        config: payload.config,
        name: payload.config.filename,
        importance: payload.importance,
        quantity: payload.quantity,
        pathKey: payload.sourceFile.pathKey,
        parentPathKey: payload.parentPathKey,
        jobPathKey: payload.pathKey,
        sourceFile: payload.sourceFile
      })
    },
    'SET_NESTED_FILE_URL' (state, payload) {
      Vue.set(state.filesNested[payload.sourceFilePathKey], 'downloadUrl', payload.url)
    },
    'SET_NESTED_FILE_CONFIG' (state, payload) {
      Vue.set(state.filesNested[payload.pathKey], 'config', payload.config)
    },
    'SET_NESTED_FILE_NAME' (state, name) {
      Vue.set(state, 'filesNestedJobName', name)
    },
    'SET_COMPARE_FILE' (state, payload) {
      const { jobOutputPathKey } = payload

      if (!state.filesCompare[jobOutputPathKey]) {
        Vue.set(state.filesCompare, jobOutputPathKey, payload)
      }
    },
    'SET_NESTED_COMPARE_FILE' (state, payload) {
      const { jobOutputPathKey } = payload

      if (!state.nestedFilesCompare[jobOutputPathKey]) {
        Vue.set(state.nestedFilesCompare, jobOutputPathKey, payload)
      }
    },
    'REMOVE_COMPARE_FILE' (state, payload) {
      const { jobOutputPathKey } = payload

      if (state.filesCompare[jobOutputPathKey]) {
        Vue.delete(state.filesCompare, jobOutputPathKey)
      }
    },
    'REMOVE_NESTED_COMPARE_FILE' (state, payload) {
      const { jobOutputPathKey } = payload

      if (state.nestedFilesCompare[jobOutputPathKey]) {
        Vue.delete(state.nestedFilesCompare, jobOutputPathKey)
      }
    },
    'RESET_COMPARE_DATA' (state) {
      Vue.set(state, 'filesCompare', {})
      Vue.set(state, 'nestedFilesCompare', {})
    },
    'REMOVE_COMPARE_DATA' (state, payload) {
      if (state.filesCompare[payload]) {
        Vue.delete(state.filesCompare, payload)
      } else if (state.nestedFilesCompare[payload]) {
        Vue.delete(state.nestedFilesCompare, payload)
      }
    },
    'UPDATE_DESIRED_QUANTITY' (state, payload) {
      Vue.set(state.filesNested[payload.pathKey], 'quantity', Number(payload.value))
    },
    'UPDATE_RELATIVE_IMPORTANCE' (state, payload) {
      Vue.set(state.filesNested[payload.pathKey], 'importance', Number(payload.value))
    },
    'RESET_NESTED_DATA' (state) {
      Vue.set(state, 'filesNested', {})
      Vue.set(state, 'filesNestedJobName', '')
    },
    'SET_URL_CONFIG' (state, payload) {
      Vue.set(state.config, 'API_URL', payload.API_URL)
      Vue.set(state.config, 'API_TRIALS_URL', payload.API_TRIALS_URL)
      Vue.set(state.config, 'AUTH0_CALLBACK_URL', payload.AUTH0_CALLBACK_URL)
    },
    'TOGGLE_GETTING_FILE_URL' (state, flag) {
      Vue.set(state, 'gettingFileURL', flag)
    },
    'SET_WEBGL_STATUS' (state, status) {
      Vue.set(state, 'webGL', status)
    }
  },
  getters: {
    filesFormatted: (state) => {
      let parentJobs = []
      let filesFormatted = []
      const jobs = state.jobs
      if (!(jobs && jobs.sourceFiles)) {
        return []
      }
      filesFormatted = [].concat(state.jobs.sourceFiles.filter((file) => {
        // Filter files by not empty jobs
        return file.jobs.length > 0
      })).map(file => {
        // Sort file jobs by created date
        file.jobs.sort((prev, next) => {
          return new Date(next.createdAt) - new Date(prev.createdAt)
        })
        return file
      }).map((file) => {
        // Build file output
        let sortDate = file.jobs.find((job) => {
          return !(job.isNested || job.parentPathKey)
        })
        if (!sortDate) {
          sortDate = file.jobs[0].createdAt
        } else {
          sortDate = sortDate.createdAt
        }
        let archivedCount = 0
        let unarchivedCount = 0
        const fileData = {
          id: file.id,
          name: file.name,
          pathKey: file.pathKey,
          jobs: file.jobs.map((job) => {
            archivedCount += job.archived.count
            unarchivedCount += job.unarchived.count
            let jobFormatted = {
              filename: file.name,
              sourceFilePathKey: file.pathKey,
              id: file.id,
              jobId: job.id,
              config: job.config,
              jobPathKey: job.pathKey,
              parentPathKey: job.parentPathKey,
              isNested: !!(job.isNested || job.nestedJobName),
              nestedJobName: job.nestedJobName,
              nestedJobs: [],
              jobDescription: job.jobDescription,
              isArchived: job.isArchived,
              archived: job.archived.count,
              unarchived: job.unarchived.count,
              createdAt: job.createdAt,
              updatedAt: job.updatedAt,
              outputsLoaded: job.outputsLoaded,
              jobOutputs: job.jobOutputs ? job.jobOutputs.map(jobOutput => {
                const outputFilesKeys = {}
                const outputFiles = jobOutput.outputFiles.filter((file) => {
                  if (!outputFilesKeys.hasOwnProperty(file.name)) {
                    outputFilesKeys[file.name] = true
                    return true
                  }
                  return false
                })
                return {
                  id: jobOutput.id,
                  objectiveId: jobOutput.objectiveId,
                  analysisId: jobOutput.analysisId,
                  jobOutputPathKey: jobOutput.pathKey,
                  isArchived: jobOutput.isArchived,
                  'status': jobOutput.status,
                  progress: jobOutput.progress,
                  paidfor: !!(jobOutput.credit || jobOutput.subscription),
                  outputFiles
                }
              }) : null,
              jobOutputsCount: job.jobOutputsCount
            }
            if ((job.isNested || job.nestedJobName) && job.jobOutputsCount > 0) {
              parentJobs.push({
                ...jobFormatted,
                sortDate: jobFormatted.createdAt
              })
            }
            return jobFormatted
          }),
          isNested: false,
          sortDate
        }
        Vue.set(fileData, 'archived', archivedCount)
        Vue.set(fileData, 'unarchived', unarchivedCount)
        return fileData
      })
      // Find nested jobs
      filesFormatted.forEach((file) => {
        file.jobs.forEach((job) => {
          const parentJob = parentJobs.find((pJob) => {
            return pJob.jobPathKey === job.parentPathKey
          })
          if (parentJob) {
            parentJob.nestedJobs.push(job)
          }
        })
      })
      // Filter file jobs by not nested job and filter out not started jobs
      filesFormatted = filesFormatted.map((file) => {
        return {
          ...file,
          jobs: file.jobs.filter((job) => {
            return !(job.isNested || job.parentPathKey) && job.jobOutputsCount > 0
          })
        }
      })
      filesFormatted = filesFormatted.filter((file) => file.jobs.length > 0)
      // Сompound and sort files
      filesFormatted = filesFormatted.concat(parentJobs)
      filesFormatted.sort((a, b) => {
        return new Date(b.sortDate) - new Date(a.sortDate)
      })
      return filesFormatted
    },
    jobById: (state) => (fileId, jobPathKey) => {
      const file = state.jobs.sourceFiles.find((sourceFile) => {
        return sourceFile.id === fileId
      })
      if (file) {
        return file.jobs.find((job) => {
          return job.pathKey === jobPathKey
        })
      }
    },
    materials: (state) => {
      return state.materials.concat(state.customMaterials, {name: 'Custom Material...'})
    },
    predefinedMaterials: (state) => {
      return state.materials.length
    },
    predefinedPrinters: (state) => {
      return state.printers.length
    },
    printers: (state) => {
      return state.printers.concat(state.customPrinters, {name: 'Custom Printer...'})
    }
  },
  modules: {
    billing: billing,
    user: user,
    _jobs: jobs,
    notifications: notifications
  }
})

export default store



// WEBPACK FOOTER //
// ./src/store/index.js